import { BookOpenCheck, CalendarDays, ClipboardCheck, FileOutput, LayoutTemplate, LogOut, MoreHorizontal, Settings, WandSparkles } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { api, clearOfflineSnapshots } from "../api";
import { useI18n } from "../i18n";
import type { Session } from "../types";
import { useOnlineStatus } from "../useOnlineStatus";
import { BrandMark } from "./BrandMark";
import { HelpAssistant } from "./HelpAssistant";
import { LanguageSelect } from "./LanguageSelect";
import { canUseSetupGuide, readSetupGuideState, SetupGuide, setupGuideCopy } from "./SetupGuide";

const navItems = [
  {path:"/",label:"today",icon:CalendarDays,end:true,permission:"course:read"},
  {path:"/courses",label:"courses",icon:BookOpenCheck,end:false,permission:"course:read"},
  {path:"/tasks",label:"tasks",icon:ClipboardCheck,end:false,permission:"task:read"},
  {path:"/templates",label:"templates",icon:LayoutTemplate,end:false,permission:"course:write"},
  {path:"/outputs",label:"outputs",icon:FileOutput,end:false,permission:"export:create"},
  {path:"/settings",label:"settings",icon:Settings,end:false,permission:"center:configure"}
] as const;

export function AppShell({session,children}:{session:Session;children:ReactNode}) {
  const {t,language,setLanguage}=useI18n();
  const navigate=useNavigate();
  const location=useLocation();
  const queryClient=useQueryClient();
  const online=useOnlineStatus();
  const canConfigure=canUseSetupGuide(session.permissions);
  const [mobileMoreOpen,setMobileMoreOpen]=useState(false);
  const [guide,setGuide]=useState(()=>{const saved=readSetupGuideState(session.user.id);return {open:canConfigure&&saved===null,startAt:saved?.status==="paused"?saved.step:0,runId:0}});
  const openSetupGuide=()=>{const saved=readSetupGuideState(session.user.id);setGuide((current)=>({open:true,startAt:saved?.status==="paused"?saved.step:0,runId:current.runId+1}))};
  const signOut=async()=>{await api<void>("/api/v1/auth/logout",{method:"POST"});clearOfflineSnapshots();queryClient.clear();navigate("/login",{replace:true})};
  const initials=session.user.displayName.split(" ").map((part)=>part[0]).join("").slice(0,2);
  const allowedNavItems=navItems.filter((item)=>session.permissions.includes(item.permission));
  const mobilePrimary=allowedNavItems.filter((item)=>["/","/courses","/tasks","/outputs"].includes(item.path));
  const mobileMore=allowedNavItems.filter((item)=>!["/","/courses","/tasks","/outputs"].includes(item.path));
  const moreLabel={en:"More",de:"Mehr",es:"Más",fr:"Plus"}[language];
  const moreActive=mobileMore.some((item)=>location.pathname===item.path||location.pathname.startsWith(`${item.path}/`));
  useEffect(()=>setMobileMoreOpen(false),[location.pathname]);
  useEffect(()=>{if(!mobileMoreOpen)return;const close=(event:KeyboardEvent)=>{if(event.key==="Escape")setMobileMoreOpen(false)};window.addEventListener("keydown",close);return()=>window.removeEventListener("keydown",close)},[mobileMoreOpen]);
  return <div className="app-shell">
    {!online&&<div className="offline-banner">{t("offline")}</div>}
    <aside className="sidebar">
      <BrandMark compact />
      <nav aria-label={t("primaryNavigation")}>
        {navItems.filter((item)=>session.permissions.includes(item.permission)).map(({path,label,icon:Icon,end})=><NavLink key={path} to={path} end={end}><Icon/><span>{t(label)}</span></NavLink>)}
      </nav>
      <div className="sidebar-foot"><p>{t("courseSafety")}</p><span>v0.1 · Standalone</span></div>
    </aside>
    <div className="shell-body">
      <header className="topbar">
        <div className="mobile-brand"><BrandMark compact /></div>
        <div className="tenant-summary"><strong>{session.tenant.displayName}</strong><span>Course operations</span></div>
        <div className="topbar-actions">{canConfigure&&<button className="setup-guide-trigger" type="button" onClick={openSetupGuide}><WandSparkles/><span>{setupGuideCopy[language].trigger}</span></button>}<HelpAssistant onStartSetupGuide={canConfigure?openSetupGuide:undefined}/><LanguageSelect/><div className="user-summary"><span className="avatar">{initials}</span><span>{session.user.displayName}<small>{session.membership.role.replaceAll("_"," ")}</small></span></div><button className="icon-button" onClick={signOut} aria-label={t("signOut")}><LogOut/></button></div>
      </header>
      <main id="main-content" className="app-main">{children}</main>
      <nav className="mobile-nav" aria-label={t("primaryNavigation")}>
        {mobilePrimary.map(({path,label,icon:Icon,end})=><NavLink key={path} to={path} end={end}><Icon/><span>{t(label)}</span></NavLink>)}
        {mobileMore.length>0&&<><button className={moreActive?"active":""} type="button" aria-expanded={mobileMoreOpen} aria-controls="mobile-more-menu" onClick={()=>setMobileMoreOpen((open)=>!open)}><MoreHorizontal/><span>{moreLabel}</span></button>{mobileMoreOpen&&<div id="mobile-more-menu" className="mobile-more-menu">{mobileMore.map(({path,label,icon:Icon,end})=><NavLink key={path} to={path} end={end}><Icon/><span>{t(label)}</span></NavLink>)}</div>}</>}
      </nav>
      {canConfigure&&<SetupGuide
        open={guide.open}
        startAt={guide.startAt}
        runId={guide.runId}
        userId={session.user.id}
        language={language}
        onLanguageChange={setLanguage}
        onClose={()=>setGuide((current)=>({...current,open:false}))}
      />}
    </div>
  </div>;
}
